// RHSidebar — navigation verticale unifiée pour la section RH
// Remplace les deux architectures divergentes (rhGroups Patron/SM vs rhTabs Manager/Employé).
// La sidebar est filtrée selon les rôles autorisés pour chaque entrée.
import React, { useState } from 'react';
import {
  Users, UserPlus, IdCard, Calendar, CalendarCheck, FileText,
  TrendingUp, Briefcase, Download, ClipboardList, ChevronLeft, ChevronRight,
  Menu, X
} from 'lucide-react';

// ============ STRUCTURE DE LA NAVIGATION RH ============
// Chaque entrée :
//   - id : identifiant interne (correspond à rhSubView ou un detail)
//   - label : libellé affiché
//   - Icon : icône lucide
//   - roles : liste des rôles autorisés
//   - badge : (optionnel) clé pour récupérer un compteur dans badges = { [key]: number }
//
// Les sections sont 3 méta-groupes (Personnel, Temps & Congés, Documents & Admin)
const SECTIONS = [
  {
    id: 'personnel',
    label: 'Personnel',
    Icon: Users,
    items: [
      {
        id: 'employees',
        label: 'Liste des employés',
        Icon: Users,
        roles: ['Patron', 'Super Manager', 'Manager'],
      },
      {
        id: 'inscriptions',
        label: 'Inscriptions',
        Icon: UserPlus,
        roles: ['Patron', 'Super Manager'],
        badgeKey: 'inscriptions_en_attente',
      },
    ],
  },
  {
    id: 'temps_conges',
    label: 'Temps & Congés',
    Icon: Calendar,
    items: [
      {
        id: 'conges',
        label: 'Congés',
        Icon: Calendar,
        roles: ['Patron', 'Super Manager', 'Manager', 'Employé'],
        badgeKey: 'conges_en_attente',
      },
      {
        id: 'solde_cp',
        label: 'Solde CP',
        Icon: CalendarCheck,
        roles: ['Patron', 'Super Manager', 'Manager'],
      },
      {
        id: 'note_rh',
        label: 'Note RH Congés',
        Icon: FileText,
        roles: ['Patron', 'Super Manager', 'Manager', 'Employé'],
      },
      {
        id: 'modulation',
        label: 'Modulation',
        Icon: TrendingUp,
        roles: ['Patron', 'Super Manager', 'Manager'],
      },
    ],
  },
  {
    id: 'documents_admin',
    label: 'Documents & Admin',
    Icon: Briefcase,
    items: [
      {
        id: 'documents',
        label: 'Documents RH',
        Icon: FileText,
        roles: ['Patron', 'Super Manager', 'Manager', 'Employé', 'Extra'],
      },
      {
        id: 'demandes',
        label: 'Demandes',
        Icon: ClipboardList,
        roles: ['Patron', 'Super Manager', 'Manager', 'Employé'],
        badgeKey: 'demandes_ouvertes',
      },
      {
        id: 'export_paie',
        label: 'Export Paie',
        Icon: Download,
        roles: ['Patron', 'Super Manager'],
      },
      {
        id: 'export_hcr',
        label: 'Export HCR',
        Icon: Download,
        roles: ['Patron', 'Super Manager'],
      },
    ],
  },
];

export function getDefaultRHView(role) {
  // Pour chaque rôle, première entrée accessible dans la sidebar
  for (const section of SECTIONS) {
    for (const item of section.items) {
      if (item.roles.includes(role)) return item.id;
    }
  }
  return 'documents';
}

const RHSidebar = ({ role, activeId, onSelect, badges = {} }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Filtrer les sections selon les rôles autorisés
  const filteredSections = SECTIONS
    .map(section => ({
      ...section,
      items: section.items.filter(item => item.roles.includes(role)),
    }))
    .filter(section => section.items.length > 0);

  // Trouve la section active pour le badge mobile
  const activeItem = filteredSections.flatMap(s => s.items).find(i => i.id === activeId);

  const Badge = ({ value }) => {
    if (!value) return null;
    return (
      <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-semibold rounded-full bg-red-500 text-white">
        {value > 99 ? '99+' : value}
      </span>
    );
  };

  // ============ SIDEBAR DESKTOP ============
  const SidebarContent = ({ onItemClick, allowCollapse = true }) => (
    <nav className="flex flex-col gap-5">
      {filteredSections.map(section => (
        <div key={section.id}>
          {!collapsed && (
            <p className="px-3 mb-1.5 text-[10px] uppercase tracking-wider font-bold text-text-muted">
              {section.label}
            </p>
          )}
          <ul className="space-y-0.5">
            {section.items.map(item => {
              const Icon = item.Icon;
              const isActive = item.id === activeId;
              const badge = item.badgeKey ? badges[item.badgeKey] : 0;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => { onSelect(item.id); onItemClick && onItemClick(); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 min-h-[40px] rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-indigo-600 text-white shadow-sm'
                        : 'text-text-secondary hover:bg-elevated-1 hover:text-text-primary'
                    } ${collapsed ? 'justify-center' : ''}`}
                    aria-current={isActive ? 'page' : undefined}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                    {!collapsed && <Badge value={badge} />}
                    {collapsed && badge ? (
                      <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full" aria-hidden="true" />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      {allowCollapse && (
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          className="mt-auto flex items-center justify-center gap-1 px-3 py-2 text-xs text-text-muted hover:text-text-primary hover:bg-elevated-1 rounded-lg transition-colors"
          aria-label={collapsed ? 'Étendre la barre latérale' : 'Réduire la barre latérale'}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <><ChevronLeft className="w-4 h-4" /><span>Réduire</span></>}
        </button>
      )}
    </nav>
  );

  return (
    <>
      {/* ============ DECLENCHEUR MOBILE ============ */}
      <div className="lg:hidden mb-3">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-elevated-2 border border-border-subtle rounded-lg text-text-primary hover:bg-elevated-1 transition-colors min-h-[48px]"
          aria-label="Ouvrir la navigation RH"
        >
          <span className="flex items-center gap-2.5">
            <Menu className="w-5 h-5" aria-hidden="true" />
            {activeItem ? (
              <span className="font-medium">{activeItem.label}</span>
            ) : (
              <span>Menu RH</span>
            )}
          </span>
          <ChevronRight className="w-4 h-4 text-text-muted" aria-hidden="true" />
        </button>
      </div>

      {/* ============ DRAWER MOBILE ============ */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 flex"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation RH"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <aside className="relative bg-surface w-72 max-w-[85vw] h-full overflow-y-auto p-4 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-text-primary">Navigation RH</h2>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="p-2 text-text-muted hover:text-text-primary hover:bg-elevated-1 rounded-lg min-h-[40px] min-w-[40px] flex items-center justify-center"
                aria-label="Fermer"
              >
                <X className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>
            <SidebarContent onItemClick={() => setMobileOpen(false)} allowCollapse={false} />
          </aside>
        </div>
      )}

      {/* ============ SIDEBAR DESKTOP ============ */}
      <aside
        className={`hidden lg:flex flex-col flex-shrink-0 bg-elevated-2 border border-border-subtle rounded-xl p-3 self-start sticky top-4 transition-all duration-200 ${collapsed ? 'w-[68px]' : 'w-60'}`}
        aria-label="Navigation RH"
      >
        <SidebarContent />
      </aside>
    </>
  );
};

export default RHSidebar;
